import { NotificationOutlined } from '@ant-design/icons';
import {
  Alert,
  Card,
  List,
  message,
  notification,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../UserContext';
import { BaseComponentProps } from '../../types';
import { formatMuteRule, MuteRule } from './formatMuteRule';
import { getAgentTimezone } from './utils';

const { Paragraph, Text } = Typography;

interface MuteRulesResponse {
  agent_id: string;
  rules: MuteRule[];
}

interface ConflictResponse {
  error: 'conflict';
  conflicting_rule: MuteRule;
}

const EMPTY_STATE_COPY =
  'No mute rules configured. Daily syncs send on all days during business hours. End-users can set up mute rules by texting their agent (e.g., "don\'t send daily syncs on weekends").';

/**
 * Build a display label for a timezone, appending an abbreviation (e.g.,
 * "America/Los_Angeles (PST)") when `Intl.DateTimeFormat` can produce one.
 * The abbreviation reflects *today's* UTC offset in the given zone, so in
 * a DST-observing zone it may not match the offset at the time a rule
 * actually fires in another season.
 */
function formatTimezoneLabel(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const abbr = parts.find(p => p.type === 'timeZoneName')?.value;
    if (abbr && abbr !== timezone) {
      return `${timezone} (${abbr})`;
    }
  } catch {
    // Invalid IANA name — fall through and return the raw value.
  }
  return timezone;
}

export const MuteScheduleCard = ({
  agent,
  apiUrl,
  clientConfig,
}: BaseComponentProps): JSX.Element => {
  const user = useContext(UserContext);
  const [rules, setRules] = useState<MuteRule[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Set (not a single ID) so that a user who kicks off toggles on multiple
  // rules in quick succession can't accidentally re-enable the first row's
  // Switch mid-PATCH and fire a duplicate request against it.
  const [togglingRuleIds, setTogglingRuleIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const agentTimezone = getAgentTimezone(agent, clientConfig);
  const timezoneLabel = formatTimezoneLabel(agentTimezone);

  const fetchRules = async (signal?: AbortSignal): Promise<void> => {
    if (!user?.accessToken) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(
        `${apiUrl}/api/agent/${agent.id}/notification-mute-rules`,
        {
          headers: { Authorization: `Bearer ${user.accessToken}` },
          signal,
        },
      );
      if (!res.ok) {
        throw new Error(`Failed to load mute rules (${res.status})`);
      }
      const data = (await res.json()) as MuteRulesResponse;
      setRules(data.rules);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      setError(
        err instanceof Error ? err.message : 'Failed to load mute rules',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    void fetchRules(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id]);

  const replaceRule = (updated: MuteRule): void => {
    setRules(previous =>
      previous == null
        ? previous
        : previous.map(r => (r.id === updated.id ? updated : r)),
    );
  };

  const handleToggle = async (
    rule: MuteRule,
    nextEnabled: boolean,
  ): Promise<void> => {
    if (!user?.accessToken) {
      void message.error('Not authenticated');
      return;
    }

    // Optimistic update — flip the row immediately so the Switch animates,
    // and revert on any non-2xx response.
    replaceRule({ ...rule, enabled: nextEnabled });
    setTogglingRuleIds(previous => {
      const next = new Set(previous);
      next.add(rule.id);
      return next;
    });

    try {
      const res = await fetch(
        `${apiUrl}/api/agent/${agent.id}/notification-mute-rules/${rule.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({ enabled: nextEnabled }),
        },
      );

      if (res.ok) {
        const updated = (await res.json()) as MuteRule;
        replaceRule(updated);
        return;
      }

      replaceRule(rule);

      if (res.status === 409) {
        const body = (await res
          .json()
          .catch(() => null)) as ConflictResponse | null;
        const description = body?.conflicting_rule
          ? `This rule overlaps with another enabled rule: ${formatMuteRule(body.conflicting_rule)}. Disable the conflicting rule first.`
          : 'Disable the conflicting rule first.';
        notification.error({
          key: `mute-conflict-${rule.id}`,
          message: 'Cannot re-enable — conflicts with another rule',
          description,
          duration: 10,
        });
        return;
      }

      if (res.status === 404) {
        void message.error('Rule no longer exists');
        await fetchRules();
        return;
      }

      if (res.status === 403) {
        void message.error(
          "You do not have permission to modify this agent's mute rules",
        );
        return;
      }

      void message.error('Failed to update rule');
    } catch {
      replaceRule(rule);
      void message.error('Failed to update rule');
    } finally {
      setTogglingRuleIds(previous => {
        if (!previous.has(rule.id)) {
          return previous;
        }
        const next = new Set(previous);
        next.delete(rule.id);
        return next;
      });
    }
  };

  return (
    <Card
      bodyStyle={{ padding: 20 }}
      bordered
      hoverable
      style={{
        boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
        borderRadius: 12,
      }}
      title={
        <>
          <NotificationOutlined style={{ color: '#5b1eaf', marginRight: 8 }} />
          Daily Sync Mute Schedule
        </>
      }
    >
      <Paragraph style={{ marginBottom: 4 }} type="secondary">
        Rules that block daily-sync messages for this agent.
      </Paragraph>
      <Paragraph style={{ marginBottom: 4 }} type="secondary">
        Times are in agent local time: <Text strong>{timezoneLabel}</Text>
      </Paragraph>
      <Paragraph style={{ marginBottom: 16 }} type="secondary">
        End-users add and remove rules by texting their agent.
      </Paragraph>

      <Spin spinning={loading}>
        {!loading && error != null && (
          <Alert
            description={error}
            message="Failed to load mute rules"
            showIcon
            type="error"
          />
        )}

        {!loading && error == null && rules != null && rules.length === 0 && (
          <Alert
            description={EMPTY_STATE_COPY}
            message="No mute rules"
            showIcon
            type="info"
          />
        )}

        {!loading && error == null && rules != null && rules.length > 0 && (
          <List
            dataSource={rules}
            itemLayout="horizontal"
            renderItem={rule => {
              const isToggling = togglingRuleIds.has(rule.id);
              const nextEnabled = !rule.enabled;
              const ruleDescription = formatMuteRule(rule);
              return (
                <List.Item
                  actions={[
                    <Switch
                      aria-label={`${rule.enabled ? 'Disable' : 'Enable'} mute rule: ${ruleDescription}`}
                      checked={rule.enabled}
                      disabled={isToggling}
                      key="toggle"
                      loading={isToggling}
                      onChange={() => void handleToggle(rule, nextEnabled)}
                      size="small"
                    />,
                  ]}
                  style={{ opacity: rule.enabled ? 1 : 0.55 }}
                >
                  <Text>{ruleDescription}</Text>
                  {!rule.enabled && (
                    <Tag color="default" style={{ marginLeft: 8 }}>
                      Disabled
                    </Tag>
                  )}
                </List.Item>
              );
            }}
          />
        )}
      </Spin>
    </Card>
  );
};
